/**
 * Indian-numbering "amount in words" — used on the payslip footer
 * (e.g., "In words: Twenty Seven Thousand Three Hundred Fifty Five Only ₹").
 *
 * Mirrors backend/src/utils/number-to-words.util.ts. Keep in sync.
 */

const ones = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen',
  'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];

const tens = [
  '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty',
  'Sixty', 'Seventy', 'Eighty', 'Ninety',
];

function twoDigits(n: number): string {
  if (n < 20) return ones[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return o === 0 ? tens[t] : `${tens[t]} ${ones[o]}`;
}

function threeDigits(n: number): string {
  if (n === 0) return '';
  if (n < 100) return twoDigits(n);
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return rest === 0
    ? `${ones[h]} Hundred`
    : `${ones[h]} Hundred ${twoDigits(rest)}`;
}

export function numberToWords(amount: number): string {
  if (!Number.isFinite(amount)) return 'Zero';
  const rounded = Math.round(amount * 100) / 100;
  const rupees = Math.floor(rounded);
  const paise = Math.round((rounded - rupees) * 100);

  if (rupees === 0 && paise === 0) return 'Zero';

  const crore = Math.floor(rupees / 10_000_000);
  const lakh = Math.floor((rupees % 10_000_000) / 100_000);
  const thousand = Math.floor((rupees % 100_000) / 1000);
  const remainder = rupees % 1000;

  const parts: string[] = [];
  if (crore > 0) parts.push(`${threeDigits(crore)} Crore`);
  if (lakh > 0) parts.push(`${threeDigits(lakh)} Lakh`);
  if (thousand > 0) parts.push(`${threeDigits(thousand)} Thousand`);
  if (remainder > 0) parts.push(threeDigits(remainder));

  let result = parts.join(' ').trim();
  if (!result) result = 'Zero';

  if (paise > 0) {
    result += ` and ${twoDigits(paise)} Paise`;
  }
  return result;
}

export function numberToWordsWithOnly(amount: number): string {
  return `${numberToWords(amount)} Only`;
}
